import type { AdminOrder } from "@lumora/bridge";
import { errorMessage } from "@lumora/bridge";
import { adminOrderRows, DataTable, LivePanel, OrderFlashBanner, useViewModel } from "@lumora/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSiteOpsVm } from "./platformViewModels.js";
import { PlatformPageHeader } from "./PlatformPageHeader.js";

type PrintTarget = "real" | "sandbox";

const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function PlatformOpsPage() {
  const [slug, setSlug] = useState("");
  const [target, setTarget] = useState<PrintTarget>("real");
  const [placeMsg, setPlaceMsg] = useState<string | null>(null);
  const [placeErr, setPlaceErr] = useState<string | null>(null);
  const [placing, setPlacing] = useState(false);
  const vm = useSiteOpsVm();
  const { orders } = useViewModel(vm);
  const [flashOrder, setFlashOrder] = useState<AdminOrder | null>(null);
  const seenIds = useRef<Set<string>>(new Set());
  const primed = useRef(false);

  const slugOk = slugPattern.test(slug.trim());

  const onSlug = (next: string) => {
    setSlug(next);
    seenIds.current = new Set();
    primed.current = false;
    vm.clearOrders();
    setFlashOrder(null);
  };

  const loadOrders = useCallback(async () => {
    const typed = slug.trim();
    if (!slugPattern.test(typed)) {
      vm.clearOrders();
      return;
    }
    const next = await vm.loadOrders(typed);

    if (!primed.current) {
      next.forEach((o) => seenIds.current.add(o.id));
      primed.current = true;
      return;
    }

    for (const o of next) {
      if (!seenIds.current.has(o.id)) {
        seenIds.current.add(o.id);
        setFlashOrder(o);
        try {
          const ctx = new AudioContext();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = 880;
          gain.gain.value = 0.08;
          osc.start();
          osc.stop(ctx.currentTime + 0.25);
        } catch {
          // ponytail: no audio if browser blocks autoplay
        }
        break;
      }
    }
  }, [vm, slug]);

  useEffect(() => {
    if (!flashOrder) return;
    const id = setTimeout(() => setFlashOrder(null), 30_000);
    return () => clearTimeout(id);
  }, [flashOrder]);

  const orderRows = useMemo(() => adminOrderRows(orders, "ops"), [orders]);
  const flashRow = flashOrder ? orders.findIndex((o) => o.id === flashOrder.id) : -1;

  const place = () => {
    if (!slugOk || placing) return;
    setPlaceMsg(null);
    setPlaceErr(null);
    setPlacing(true);
    void vm
      .placeTestOrder(slug.trim(), target)
      .then((order) => {
        setPlaceMsg(
          `Test order #${order.number} queued for the ${target === "sandbox" ? "sandbox printer" : "real kitchen"}.`,
        );
      })
      .catch((e: unknown) => setPlaceErr(errorMessage(e, "Test order failed")))
      .finally(() => setPlacing(false));
  };

  return (
    <div className="site-platform">
      <OrderFlashBanner
        order={
          flashOrder
            ? {
                number: flashOrder.number,
                customerName: flashOrder.customerName,
                pickupTime: flashOrder.pickupTime,
                totalCents: flashOrder.totalCents,
              }
            : null
        }
        onDismiss={() => setFlashOrder(null)}
      />
      <PlatformPageHeader
        title="Live ops"
        lead="Type a site slug to watch its orders and send a test ticket."
      />

      <section className="ops-panel" data-api-playground>
        <h2>API playground</h2>
        <p className="muted">
          Platform admin test order. No card charge. The line comes from that site&apos;s menu.
          Sandbox prints only on that site&apos;s sandbox device. Real reaches the production
          kitchen pool.
        </p>
        <label>
          Site slug
          <input
            name="site-slug"
            value={slug}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => onSlug(e.target.value)}
          />
        </label>
        <fieldset>
          <legend>Printer</legend>
          <label>
            <input
              type="radio"
              name="print-target"
              value="real"
              checked={target === "real"}
              onChange={() => setTarget("real")}
            />
            Real kitchen
          </label>
          <label>
            <input
              type="radio"
              name="print-target"
              value="sandbox"
              checked={target === "sandbox"}
              onChange={() => setTarget("sandbox")}
            />
            Sandbox device
          </label>
        </fieldset>
        <button type="button" className="ops-btn" disabled={!slugOk || placing} onClick={place}>
          Place test order
        </button>
        {placeMsg ? <p className="muted">{placeMsg}</p> : null}
        {placeErr ? <p className="ops-error">{placeErr}</p> : null}
      </section>

      <p className="site-platform-aside muted">
        New orders flash here and in <Link to="/platform/activity">Activity</Link>. Wipe test
        orders from the site admin tools when you are done.
      </p>

      <div className="ops-grid">
        <LivePanel title={slugOk ? `${slug.trim()} orders` : "Orders"} load={loadOrders} intervalMs={2500}>
          <DataTable
            columns={["#", "Name", "Pickup", "Status", "Channel", "Total", "Time"]}
            rows={orderRows}
            empty={slugOk ? "No orders yet." : "Type a site slug to load orders."}
            {...(flashRow >= 0 ? { highlightRow: flashRow } : {})}
          />
        </LivePanel>
      </div>
    </div>
  );
}
